import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FaBolt, FaFire, FaTimes } from 'react-icons/fa';
import '../../../styles/ListenerProfileCard.css';
import { useLocale } from '../../../contexts/LocaleContext.jsx';
import { NAME_PAINT_SURFACES, namePaintStyle } from '../../../_helpers/namePaint.js';
import { fetchListenerCard, nameInitials, skinTag } from '../../../_helpers/listenerCard.js';
import { canPreviewCardSkin, cardSkinLook } from '../../../_helpers/cardSkin.js';
import { canTryOn } from '../../../_helpers/skinTryOn.js';
import { isFxItemType } from '../../../_helpers/fxItems.js';
import '../../../styles/FxTryOnPanel.css';
import PaintedName from './PaintedName.jsx';

/** A skin's label: "Earned", "Limited · #12 of 250" (`withEdition`), "Standard", or its rarity. */
export const SkinTag = ({ cardSkin, withEdition = false }) => {
  const { t, format } = useLocale();
  const tag = skinTag(cardSkin);
  if (!tag) return null;
  let text = t(`stream.chat.card.tag.${tag.kind}`);
  if (tag.kind === 'limited' && withEdition && tag.editionNumber) {
    text = t('stream.chat.card.tag.limitedEdition', {
      number: format.number(tag.editionNumber),
      size: format.number(tag.editionSize),
    });
  }
  return (
    <span className={`listener-card-tag listener-card-tag--${tag.kind}${withEdition ? ' listener-card-tag--chip' : ''}`} data-testid="listener-card-tag">
      {text}
    </span>
  );
};

/**
 * A skin's swatch. A name paint is drawn from its paint data; a now-playing
 * card skin is a card-shaped tile framed in its accent colour from its assets
 * (cardSkin.js, ENG-735), or its rarity colour when it has none. A vibe set
 * or boost animation (ENG-816) is an icon on its rarity colour; its live
 * preview is FxTryOnPanel's.
 */
export const SkinPreview = ({ cardSkin, large = false }) => {
  const { skin } = cardSkin;
  const paintStyle = skin.type === 'name_paint' ? namePaintStyle(skin.paint, NAME_PAINT_SURFACES.chat) : null;
  const accent = cardSkinLook(skin)?.accentColor;
  return (
    <span
      className={`listener-card-preview listener-card-preview--${skin.rarity}${large ? ' listener-card-preview--large' : ''}`}
      data-skin-type={skin.type}
      style={accent ? { '--listener-card-rarity': accent } : undefined}
      aria-hidden="true"
    >
      {skin.type === 'vibe_set' && <span className="listener-card-preview-fx"><FaFire /></span>}
      {skin.type === 'boost_animation' && <span className="listener-card-preview-fx"><FaBolt /></span>}
      {isFxItemType(skin.type) ? null : paintStyle ? (
        <span className="listener-card-preview-paint name-paint" style={paintStyle}>{nameInitials(skin.name)}</span>
      ) : (
        <span className="listener-card-preview-card">
          <span className="listener-card-preview-art" />
          <span className="listener-card-preview-lines">
            <span />
            <span />
            <span />
          </span>
        </span>
      )}
    </span>
  );
};

/**
 * The listener profile card (Skins, mockup 34, ENG-733): opened from a name
 * in the stream chat, over the chat. Avatar, painted name, the band they are
 * a regular at and their skin count, what they wear on the now-playing card,
 * and a strip of their skins (at most 12, from the server).
 *
 * `bandId` is the stream page's band, so the card names this band when the
 * listener is a regular here. `fallbackName` is the name from the chat line,
 * shown while the card loads or when it can't be loaded.
 *
 * "See all" (the skin shelf, mockup 37, ENG-739), "See what they see" (mockup
 * 35) and "Try it on" (mockup 36) each appear only when their handler is
 * passed, so nothing on the card is a dead button. On the viewer's own card
 * "See all" opens their shelf and shows once they own a skin. On another
 * listener's card it opens the skins shop (ENG-823), so it shows whatever
 * their count: the chat passes `seeAllOpensShop` for those. There is no
 * public shelf of another listener's skins. "See what they
 * see" also needs the listener to wear a now-playing card skin; "Try it on"
 * needs them to wear a skin that can be tried on (a now-playing card skin or
 * a name paint, ENG-737).
 *
 * Closes with ×, Escape, or a press outside the card.
 */
const ListenerProfileCard = ({ userId, bandId = null, fallbackName = '', onClose, onSeeAll = null, seeAllOpensShop = false, onSeeWhatTheySee = null, onTryOn = null }) => {
  const { t, format } = useLocale();
  const [state, setState] = useState({ status: 'loading', card: null, error: null });
  const [attempt, setAttempt] = useState(0);
  const cardRef = useRef(null);
  const closeRef = useRef(null);

  useEffect(() => {
    let active = true;
    setState({ status: 'loading', card: null, error: null });
    fetchListenerCard(userId, bandId)
      .then((card) => { if (active) setState({ status: 'ready', card, error: null }); })
      .catch((error) => { if (active) setState({ status: 'error', card: null, error }); });
    return () => { active = false; };
  }, [userId, bandId, attempt]);

  const close = useCallback(() => onClose?.(), [onClose]);

  useEffect(() => {
    const onPointerDown = (event) => {
      if (cardRef.current && !cardRef.current.contains(event.target)) close();
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [close]);

  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  const { card } = state;
  const name = card?.displayName || fallbackName;

  let subline = null;
  if (card) {
    const count = t('stream.chat.card.skinCount', { count: card.skinCount });
    subline = card.regularAt
      ? t('stream.chat.card.regularAtWithSkins', {
        place: t(`stream.chat.card.${card.regularAt.tier === 'superfan' ? 'superfanAt' : 'regularAt'}`, { band: card.regularAt.bandName }),
        skins: count,
      })
      : count;
  }

  return (
    <div
      ref={cardRef}
      className="listener-card"
      role="dialog"
      aria-label={t('stream.chat.card.label', { name })}
      data-testid="listener-card"
    >
      <div className="listener-card-header">
        {card?.avatarUrl ? (
          <img className="listener-card-avatar" src={card.avatarUrl} alt="" />
        ) : (
          <span className="listener-card-avatar listener-card-avatar--initials" aria-hidden="true">{nameInitials(name)}</span>
        )}
        <div className="listener-card-identity">
          <PaintedName
            name={name}
            paint={card?.namePaint}
            surface={NAME_PAINT_SURFACES.chat}
            className="listener-card-name"
          />
          {subline && <span className="listener-card-subline" data-testid="listener-card-subline">{subline}</span>}
        </div>
        <button ref={closeRef} type="button" className="listener-card-close" onClick={close} aria-label={t('stream.chat.card.close')}>
          <FaTimes aria-hidden="true" />
        </button>
      </div>

      {state.status === 'loading' && (
        <p className="listener-card-status" role="status">{t('stream.chat.card.loading')}</p>
      )}

      {state.status === 'error' && (
        <div className="listener-card-status" role="alert">
          {state.error?.status === 404 ? (
            <span>{t('stream.chat.card.notFound')}</span>
          ) : (
            <>
              <span>{t('stream.chat.card.error')}</span>
              <button type="button" className="listener-card-retry" onClick={() => setAttempt((n) => n + 1)}>
                {t('stream.chat.card.retry')}
              </button>
            </>
          )}
        </div>
      )}

      {card && (
        <>
          {card.wearing && (
            <section className="listener-card-section" data-testid="listener-card-wearing">
              <h3 className="listener-card-heading">{t('stream.chat.card.wearingNow')}</h3>
              <SkinPreview cardSkin={card.wearing} large />
              <div className="listener-card-wearing-meta">
                <span className="listener-card-wearing-name">{card.wearing.skin.name}</span>
                <SkinTag cardSkin={card.wearing} withEdition />
              </div>
            </section>
          )}

          <section className="listener-card-section" data-testid="listener-card-skins">
            <div className="listener-card-heading-row">
              <h3 className="listener-card-heading">{t('stream.chat.card.theirSkins', { total: format.number(card.skinCount) })}</h3>
              {onSeeAll && (seeAllOpensShop || card.skinCount > 0) && (
                <button type="button" className="listener-card-see-all" onClick={() => onSeeAll(card)}>
                  {t('stream.chat.card.seeAll')}
                </button>
              )}
            </div>
            {card.skins.length === 0 ? (
              <p className="listener-card-empty" data-testid="listener-card-no-skins">{t('stream.chat.card.noSkins')}</p>
            ) : (
              <ul className="listener-card-strip">
                {card.skins.map((cardSkin) => (
                  <li key={cardSkin.skin.id} className="listener-card-strip-item" data-testid="listener-card-skin">
                    <SkinPreview cardSkin={cardSkin} />
                    <span className="listener-card-strip-name">{cardSkin.skin.name}</span>
                    <SkinTag cardSkin={cardSkin} />
                  </li>
                ))}
              </ul>
            )}
          </section>

          {((onSeeWhatTheySee && canPreviewCardSkin(card)) || (onTryOn && canTryOn(card))) && (
            <div className="listener-card-actions">
              {onSeeWhatTheySee && canPreviewCardSkin(card) && (
                <button type="button" className="listener-card-action listener-card-action--primary" onClick={() => onSeeWhatTheySee(card)}>
                  {t('stream.chat.card.seeWhatTheySee')}
                </button>
              )}
              {onTryOn && canTryOn(card) && (
                <button type="button" className="listener-card-action" onClick={() => onTryOn(card)}>
                  {t('stream.chat.card.tryItOn')}
                </button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ListenerProfileCard;
